
// Parses a Trimble FXL (Feature Definition Library - the field-coding
// library format used by Trimble Access / Trimble Business Center) into
// the same shape produced by csvRowsToFeatureClasses() in Digitizing.js:
// [{name, geometryType, color, forsyningsart, classify?, attributes: [{label, type, options?}]}].
// Entirely client-side (DOMParser), no server-side preprocessing, matching
// how LER GML/WFS import already works.
//
// Real utility feature codes in the FXL files seen so far (Trimble Access
// exports from a Danish surveying workflow) are *all* <PointFeatureDefinition>
// elements, regardless of their true geometry - a line like "Foringsrør" is
// walked in the field as a sequence of points joined by start/end control
// codes, not represented by a distinct XML element. The actual geometry
// kind is only recoverable from a marker embedded in the Name attribute,
// e.g. ".      L - DVA_DanVand             (2;1)" (marker "L") or
// "807 Pt - DVA_Opmåling" (marker "Pt") - see inferGeometryFromName().
// True <LineFeatureDefinition>/<PolygonFeatureDefinition> elements (seen
// only for Trimble's own generic "_Linjer" control-code set) are honored
// directly when present, since that's an unambiguous, better signal.
//
// Categories starting with "_" (Trimble's internal line/spline/close
// control codes - "_Linjer" in both sample files) are skipped entirely;
// they aren't real features. Everything else is imported - including
// survey-control-looking categories like "Opmåling" - since silently
// guessing what to drop risks losing something the user wanted more than
// importing an extra type they can delete in one click costs them.

function attributeType(tagName){
	switch(tagName){
		case "IntegerAttribute": return "number";
		case "DoubleAttribute": return "number";
		case "DateAttribute": return "date";
		case "ListAttribute": return "enum";
		case "StringAttribute":
		default: return "text";
	}
}

function parseAttributes(featureEl){
	let attributesEl = featureEl.getElementsByTagName("Attributes")[0];
	if(!attributesEl){
		return [];
	}

	let attributes = [];
	for(let child of Array.from(attributesEl.children)){
		let label = child.getAttribute("Name");
		if(!label){
			continue;
		}

		let attr = {label, type: attributeType(child.tagName)};

		if(attr.type === "enum"){
			let items = Array.from(child.getElementsByTagName("Item"))
				.map(item => (item.textContent || "").trim())
				.filter(text => text && text !== "?");
			attr.options = items;
		}

		attributes.push(attr);
	}

	return attributes;
}

// Reads the "L" / "F" / "P" / "Pn" / "Pt" marker Trimble Access embeds in
// a PointFeatureDefinition's Name (the part before " - "), e.g.
// ".      L - DVA_DanVand (2;1)" -> "L", "807 Pt - DVA_Opmåling" -> "Pt".
function inferGeometryFromName(name){
	let beforeDash = (name || "").split(" - ")[0] || "";
	let tokens = beforeDash.trim().split(/\s+/);
	let marker = (tokens[tokens.length - 1] || "").toLowerCase();

	if(marker === "l"){
		return "line";
	}
	if(marker === "f"){
		return "surface";
	}
	return "point";
}

function argbToHex(argb){
	if(!argb || argb.length < 6){
		return null;
	}
	// FXL colors are ARGB hex (e.g. "FE000000") - drop the alpha byte.
	let rgb = argb.slice(-6);
	return "#" + rgb.toLowerCase();
}

// forsyningsart is required, not guessed: an FXL file mixes enough
// unrelated codes (survey control points, generic "other" categories) that
// keyword-guessing per feature was unreliable - the user picks one supply
// type up front (an FXL library is normally a single utility domain
// anyway, e.g. "DanVand_Silkeborg.fxl" is entirely water) and it's applied
// to every imported feature, exactly like specifying it once.
export function parseFxlDocument(doc, forsyningsart){
	let results = [];

	let featureEls = [
		...Array.from(doc.getElementsByTagName("PointFeatureDefinition")),
		...Array.from(doc.getElementsByTagName("LineFeatureDefinition")),
		...Array.from(doc.getElementsByTagName("PolygonFeatureDefinition")),
	];

	for(let el of featureEls){
		let category = el.getAttribute("Category") || "";
		if(category.startsWith("_")){
			continue;
		}

		let code = el.getAttribute("Code") || "";
		let rawName = el.getAttribute("Name") || "";
		if(!code){
			continue;
		}

		let geometryType;
		if(el.tagName === "LineFeatureDefinition"){
			geometryType = "line";
		}else if(el.tagName === "PolygonFeatureDefinition"){
			geometryType = "surface";
		}else{
			geometryType = inferGeometryFromName(rawName);
		}

		let name = category ? `${category} - ${code}` : code;
		let color = argbToHex(el.getAttribute("Color"));

		let entry = {
			name,
			geometryType,
			color: color || null, // resolved against forsyningsart color by the caller if null
			forsyningsart,
			attributes: parseAttributes(el),
		};

		if(geometryType === "line"){
			entry.classify = {enabled: false, sourceCode: null, growthMargin: 2, connectDistance: 0.15};
		}

		results.push(entry);
	}

	return results;
}

export function parseFxlText(text, forsyningsart){
	let doc = new DOMParser().parseFromString(text, "application/xml");
	if(doc.querySelector("parsererror")){
		throw new Error("FXL-filen kunne ikke tolkes som XML");
	}
	return parseFxlDocument(doc, forsyningsart);
}
